import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import CloudAppAPI from "../../api/CloudAppAPI";
import CloudAppUtils from "../../../utils/CloudAppUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

interface Props {
  payload: any;
  onSuccess: () => void;
}

const useCreateCloudAppFromFolder = () => {
  const workspaceID = useAtomValue(workspaceIdAtom);
  const queryClient = useQueryClient();
  const { tenantID }: any = useParams();

  const createCloudAppFromFolder = useMutation({
    mutationFn: async ({ payload }: Props) => {
      return CloudAppAPI.createCloudAppFromFolder(
        workspaceID,
        payload.FolderID,
        payload
      );
    },
    onSuccess: (response, params) => {
      const { payload, onSuccess } = params;

      const newCloudApp = response.data.data.app;
      const updatedWorkspace = response.data.data.workspace;
      queryClient.setQueryData<any>(
        ["workspace", workspaceID],
        (data: any) => ({
          ...data,
          workspace: updatedWorkspace,
        })
      );

      CloudAppUtils.addCloudAppToCloudApps(tenantID, queryClient, newCloudApp);
      queryClient.invalidateQueries({
        queryKey: ["workspaces", tenantID],
      });

      newToastEvent(
        "success",
        `Successfully created cloud app "${
          payload?.Title || newCloudApp?.Title
        }".`
      );
      callFunctionIfExists(onSuccess);
    },
    onError: (err: any) => {
      const status = err?.response?.status;

      if (status === 409) {
        return newToastEvent("error", "There is another app with this name.");
      }

      const errorMessage = getErrorMessage(err, "");
      const finalMessage = `An unexpected error occurred.\n(${
        errorMessage || "Unknown Error"
      })`;

      newToastEvent("error", finalMessage);
    },
  });
  return {
    createCloudAppFromFolder: createCloudAppFromFolder.mutate,
    createFromFolderSubmitting: createCloudAppFromFolder.isPending,
    error: createCloudAppFromFolder.isError,
  };
};

export default useCreateCloudAppFromFolder;
